/**
 * Page Permission Service
 *
 * 頁面級權限服務 - 管理用戶的頁面存取權限
 * 支援組織級別和用戶級別的權限授予，組織權限可被其成員繼承
 */

import { supabase } from '@shared/services/core/supabaseClient';
import {
    deriveModesFromPermissions,
    hasPagePermission as checkPagePermission,
    getDefaultPermissions,
    type NavMode
} from '@shared/config/pagePermissions';

// ============================================================================
// Type Definitions
// ============================================================================

/** 用戶頁面權限 */
export interface UserPagePermissions {
    /** 用戶直接權限 */
    direct: string[];
    /** 從組織繼承的權限 */
    inherited: string[];
    /** 合併後的所有權限 */
    all: string[];
    /** 推導出的可用模式 */
    modes: NavMode[];
}

/** 權限記錄 */
export interface PermissionRecord {
    id: number;
    target_type: 'organization' | 'user';
    target_id: string;
    permission: string;
    granted_by: string | null;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
}

/** 快取項目 */
interface CacheEntry {
    data: UserPagePermissions;
    timestamp: number;
}

// ============================================================================
// Constants
// ============================================================================

/** 快取持續時間：5 分鐘 */
const CACHE_DURATION = 5 * 60 * 1000;

/** 權限快取 */
const cache = new Map<string, CacheEntry>();

// ============================================================================
// Service
// ============================================================================

export const pagePermissionService = {
    /**
     * 清除快取
     */
    clearCache(): void {
        cache.clear();
    },

    /**
     * 清除特定用戶的快取
     */
    clearUserCache(userId: string): void {
        // 清除所有包含該 userId 的快取鍵
        for (const key of cache.keys()) {
            if (key.startsWith(userId)) {
                cache.delete(key);
            }
        }
    },

    /**
     * 獲取用戶的頁面權限
     * 合併用戶直接權限 + 所屬組織權限
     *
     * @param userId 用戶 ID
     * @param organizationIds 組織 ID 列表（通常是當前選中的組織）
     * @returns 用戶頁面權限
     */
    async getUserPagePermissions(userId: string, organizationIds: string[]): Promise<UserPagePermissions> {
        // 建立快取鍵
        const cacheKey = `${userId}_${organizationIds.sort().join(',')}`;

        // 檢查快取
        const cached = cache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
            return cached.data;
        }

        try {
            // 查詢用戶直接權限
            const { data: userPerms, error: userError } = await supabase
                .from('permissions')
                .select('permission')
                .eq('target_type', 'user')
                .eq('target_id', userId)
                .is('deleted_at', null);

            if (userError) {
                console.error('[pagePermissionService] 查詢用戶權限失敗:', userError);
            }

            // 查詢組織權限
            const { data: orgPerms, error: orgError } = await supabase
                .from('permissions')
                .select('permission')
                .eq('target_type', 'organization')
                .in('target_id', organizationIds)
                .is('deleted_at', null);

            if (orgError) {
                console.error('[pagePermissionService] 查詢組織權限失敗:', orgError);
            }

            // 整理權限列表
            const direct = (userPerms || []).map(p => p.permission);
            const inherited = (orgPerms || []).map(p => p.permission);
            const all = [...new Set([...direct, ...inherited])];

            // 從頁面權限推導可用模式
            const modes = deriveModesFromPermissions(all);

            const result: UserPagePermissions = { direct, inherited, all, modes };

            // 更新快取
            cache.set(cacheKey, { data: result, timestamp: Date.now() });

            return result;
        } catch (error) {
            console.error('[pagePermissionService] 獲取用戶權限失敗:', error);
            // 返回空權限
            return {
                direct: [],
                inherited: [],
                all: [],
                modes: deriveModesFromPermissions([])
            };
        }
    },

    /**
     * 使用 Supabase 函數獲取用戶權限（更高效）
     * 此方法使用資料庫函數，可以減少查詢次數
     */
    async getUserPagePermissionsByFunction(userId: string, organizationId: string): Promise<UserPagePermissions> {
        const cacheKey = `${userId}_${organizationId}`;

        // 檢查快取
        const cached = cache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
            return cached.data;
        }

        try {
            const { data, error } = await supabase
                .rpc('get_user_page_permissions_by_org', {
                    p_user_id: userId,
                    p_organization_id: organizationId
                });

            if (error) {
                console.error('[pagePermissionService] RPC 查詢失敗:', error);
                // 回退到一般查詢
                return this.getUserPagePermissions(userId, [organizationId]);
            }

            // 整理資料
            const direct: string[] = [];
            const inherited: string[] = [];

            (data || []).forEach((row: { permission: string; source: string }) => {
                if (row.source === 'user') {
                    direct.push(row.permission);
                } else {
                    inherited.push(row.permission);
                }
            });

            const all = [...new Set([...direct, ...inherited])];
            const modes = deriveModesFromPermissions(all);

            const result: UserPagePermissions = { direct, inherited, all, modes };

            // 更新快取
            cache.set(cacheKey, { data: result, timestamp: Date.now() });

            return result;
        } catch (error) {
            console.error('[pagePermissionService] 獲取用戶權限失敗:', error);
            return {
                direct: [],
                inherited: [],
                all: [],
                modes: deriveModesFromPermissions([])
            };
        }
    },

    /**
     * 檢查是否有特定頁面權限
     */
    hasPagePermission(permissions: string[], dir: string, page: string): boolean {
        return checkPagePermission(permissions, dir, page);
    },

    /**
     * 檢查是否可以存取特定模式
     */
    hasModeAccess(modes: NavMode[], mode: NavMode): boolean {
        return modes.includes(mode);
    },

    /**
     * 授予權限
     *
     * @param targetType 目標類型：organization 或 user
     * @param targetId 目標 ID
     * @param permission 權限字串
     * @param grantedBy 授權者
     */
    async grantPermission(
        targetType: 'organization' | 'user',
        targetId: string,
        permission: string,
        grantedBy: string
    ): Promise<void> {
        const { error } = await supabase
            .from('permissions')
            .upsert({
                target_type: targetType,
                target_id: targetId,
                permission,
                granted_by: grantedBy,
                deleted_at: null,
                updated_at: new Date().toISOString()
            }, {
                onConflict: 'target_type,target_id,permission',
                ignoreDuplicates: false
            });

        if (error) {
            console.error('[pagePermissionService] 授予權限失敗:', error);
            throw error;
        }

        this.clearCache();
    },

    /**
     * 撤銷權限（軟刪除）
     */
    async revokePermission(
        targetType: 'organization' | 'user',
        targetId: string,
        permission: string
    ): Promise<void> {
        const { error } = await supabase
            .from('permissions')
            .update({ deleted_at: new Date().toISOString() })
            .eq('target_type', targetType)
            .eq('target_id', targetId)
            .eq('permission', permission)
            .is('deleted_at', null);

        if (error) {
            console.error('[pagePermissionService] 撤銷權限失敗:', error);
            throw error;
        }

        this.clearCache();
    },

    /**
     * 為新組織授予預設權限
     */
    async grantDefaultPermissions(organizationId: string, grantedBy: string): Promise<void> {
        const defaultPerms = getDefaultPermissions();

        const records = defaultPerms.map(permission => ({
            target_type: 'organization' as const,
            target_id: organizationId,
            permission,
            granted_by: grantedBy
        }));

        const { error } = await supabase
            .from('permissions')
            .insert(records);

        if (error) {
            console.error('[pagePermissionService] 授予預設權限失敗:', error);
            throw error;
        }
    },

    /**
     * 批量授予權限
     */
    async grantPermissions(
        targetType: 'organization' | 'user',
        targetId: string,
        permissions: string[],
        grantedBy: string
    ): Promise<void> {
        const records = permissions.map(permission => ({
            target_type: targetType,
            target_id: targetId,
            permission,
            granted_by: grantedBy
        }));

        const { error } = await supabase
            .from('permissions')
            .upsert(records, {
                onConflict: 'target_type,target_id,permission',
                ignoreDuplicates: false
            });

        if (error) {
            console.error('[pagePermissionService] 批量授予權限失敗:', error);
            throw error;
        }

        this.clearCache();
    },

    /**
     * 批量撤銷權限
     */
    async revokePermissions(
        targetType: 'organization' | 'user',
        targetId: string,
        permissions: string[]
    ): Promise<void> {
        const { error } = await supabase
            .from('permissions')
            .update({ deleted_at: new Date().toISOString() })
            .eq('target_type', targetType)
            .eq('target_id', targetId)
            .in('permission', permissions)
            .is('deleted_at', null);

        if (error) {
            console.error('[pagePermissionService] 批量撤銷權限失敗:', error);
            throw error;
        }

        this.clearCache();
    },

    /**
     * 獲取目標的所有權限
     */
    async getPermissions(
        targetType: 'organization' | 'user',
        targetId: string
    ): Promise<PermissionRecord[]> {
        const { data, error } = await supabase
            .from('permissions')
            .select('*')
            .eq('target_type', targetType)
            .eq('target_id', targetId)
            .is('deleted_at', null)
            .order('permission');

        if (error) {
            console.error('[pagePermissionService] 查詢權限失敗:', error);
            throw error;
        }

        return data || [];
    },

    /**
     * 複製組織權限到另一個組織
     */
    async copyOrganizationPermissions(
        sourceOrgId: string,
        targetOrgId: string,
        grantedBy: string
    ): Promise<void> {
        // 獲取來源組織的權限
        const sourcePerms = await this.getPermissions('organization', sourceOrgId);

        // 授予目標組織相同的權限
        const permissions = sourcePerms.map(p => p.permission);
        await this.grantPermissions('organization', targetOrgId, permissions, grantedBy);
    }
};

export default pagePermissionService;
